import {
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	MenuItemProps,
	MenuProps,
} from "@material-ui/core";
import { FC, MouseEvent, ReactNode, useState, useRef, useCallback } from "react";

type ClickEvent = MouseEvent<any, any>;

type RequiredProps = {
	onClick?: (ev: ClickEvent) => void;
};

type ContextMenuProps = {
	/**
	 * Este componente funcional abrirá el menú al ser cliqueado.
	 * Es obligatorio que este componente tenga el parámetro `onClick`.
	 */
	Opener: FC<RequiredProps>;
	/** Elementos a renderizar. */
	options: DropdownOption[];
	/**  Por defecto `true`. */
	closeAfterClick?: boolean;
	/**  Por defecto `true`. */
	closeOnBlur?: boolean;
	/**  Por defecto `true`. */
	closeOnEscape?: boolean;
	/**  Por defecto `false`. */
	closeOnTab?: boolean;

	onMenuOpen?: (ev: ClickEvent) => void;
	/** Por defecto `"No hay opciones."` */
	emptyMessage?: ReactNode;
} & Partial<MenuProps>;

type CloseReason =
	| "escapeKeyDown"
	| "backdropClick"
	| "tabKeyDown";

export type DropdownOption = {
	label: ReactNode;
	icon?: ReactNode;
	helperText?: ReactNode;
	disabled?: boolean;
} & MenuItemProps;

export const DropdownMenu: FC<ContextMenuProps> = ({
	Opener,
	options,
	onMenuOpen,
	emptyMessage = "No hay opciones.",
	closeAfterClick = true,
	closeOnBlur = true,
	closeOnEscape = true,
	closeOnTab = false,
	...menuProps
}) => {
	const anchorRef = useRef<HTMLElement | null>(null);
	const [isMenuOpen, setIsMenuOpen] = useState(false);

	const handleClick = useCallback(
		async (ev: ClickEvent, o: DropdownOption) => {
			try {
				// Cerrar el menú inmediatamente si está configurado para hacerlo
				if (closeAfterClick) {
					setIsMenuOpen(false);
				}

				// Ejecutar la acción del elemento del menú
				await o.onClick?.(ev);
			} catch (error) {
				console.error('[DropdownMenu] Error in menu item click:', error);
			}
		},
		[closeAfterClick]
	);

	const handleOpen = useCallback((ev: ClickEvent) => {
		ev.persist(); // Persist the event to avoid React pooling issues
		anchorRef.current = ev.currentTarget;
		setIsMenuOpen(true);
		onMenuOpen?.(ev);
	}, [onMenuOpen]);

	const handleClose = useCallback((event: any, reason: CloseReason) => {
		const shouldClose =
			(reason === "escapeKeyDown" && closeOnEscape) ||
			(reason === "backdropClick" && closeOnBlur) ||
			(reason === "tabKeyDown" && closeOnTab);

		if (shouldClose) {
			setIsMenuOpen(false);
		}
	}, [closeOnEscape, closeOnBlur, closeOnTab]);

	return (
		<>
			<Opener onClick={(ev) => handleOpen(ev)} />
			<Menu
				{...menuProps}
				open={isMenuOpen}
				anchorEl={anchorRef.current}
				onClose={handleClose}
				keepMounted={false}
				disablePortal={false}
			>
				{options.length === 0 && (
					<MenuItem disabled>{emptyMessage}</MenuItem>
				)}
				{options.map((o, i) => {
					const {
						label,
						icon,
						helperText,
						button,
						...props
					} = o;
					return (
						<MenuItem
							{...props}
							key={`menu-item-${i}`}
							onClick={(ev) => handleClick(ev, o)}
						>
							{icon && <ListItemIcon>{icon}</ListItemIcon>}
							<ListItemText
								primary={label}
								secondary={helperText}
							/>
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
};
